import classnames from 'classnames';
import * as React from 'react';

import sharedEventListeners from '../../helpers/shared-event-listeners';
import { Feature } from './types';

export interface ProductSpecFeatureProps {
	feature: Feature;
	tooltipId: string;
}

const eventListeners = sharedEventListeners(document.body);

export const ProductSpecFeature: React.FC<
	ProductSpecFeatureProps & React.HTMLProps<HTMLTableRowElement>
> = ({ feature, tooltipId, className }) => {
	const tooltipTriggerRef = React.useRef<React.ReactNode>(null);
	const tooltipContentRef = React.useRef<HTMLDivElement>(null);

	const onRefChange = React.useCallback((node: React.ReactNode) => {
		tooltipTriggerRef.current = node; // Register tooltip trigger element

		if (tooltipTriggerRef.current) {
			['mouseenter', 'focus', 'mouseleave', 'blur'].forEach((eventName) => {
				(tooltipTriggerRef.current as HTMLButtonElement).addEventListener(
					eventName,
					(event) => {
						const targetTooltip = tooltipContentRef.current;

						if (!targetTooltip) {
							return;
						}

						// Show
						if (eventName === 'mouseenter' || eventName === 'focus') {
							targetTooltip.setAttribute('aria-hidden', 'false');
							targetTooltip.classList.remove('hidden');
						}

						// Hide
						if (eventName === 'mouseleave' || eventName === 'blur') {
							targetTooltip.setAttribute('aria-hidden', 'true');
							targetTooltip.classList.add('hidden');
						}
					}
				);
			});
		}
	}, []);

	React.useEffect(() => {
		if (!feature.rolloverTitle || feature.rolloverTitle.length === 0) {
			return undefined;
		}

		function handleKeydown(event: Event | KeyboardEvent) {
			if (
				(event as KeyboardEvent).code === 'Escape' ||
				(event as KeyboardEvent).keyCode === 27
			) {
				if (!tooltipContentRef.current) {
					return;
				}

				tooltipContentRef.current.setAttribute('aria-hidden', 'true');
				tooltipContentRef.current.classList.add('hidden');
			}
		}

		const removeKeydownListener = eventListeners.add('keydown', handleKeydown);

		return () => {
			removeKeydownListener();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<tr
			data-testid="product-spec-feature"
			className={classnames(
				'border-0 border-t border-b border-solid border-gray-30',
				className
			)}
		>
			<th className="px-1 py-2 text-sm">
				<div className="flex items-center">
					<div className="relative">
						{feature.rolloverTitle && feature.rolloverTitle.length > 0 ? (
							<React.Fragment>
								<button
									data-testid={`tooltip-trigger-${tooltipId}`}
									data-cb-spec-tooltip
									ref={(node) => onRefChange(node)}
									className="c-chat-product-spec-feature-tooltip p-1 m-0 bg-transparent border-none"
									type="button"
									aria-describedby={tooltipId}
								>
									<span
										className="ico ico-info text-cta-action"
										style={{ fontSize: '14px' }}
									></span>
									<span className="sr-only">{feature.featureName}</span>
								</button>
								<div
									data-testid={`tooltip-content-${tooltipId}`}
									ref={tooltipContentRef}
									role="tooltip"
									aria-hidden="true"
									id={tooltipId}
									className="c-feature-tooltip absolute hidden"
									style={{ width: '230px' }}
								>
									<div className="text-body-xs relative z-20 px-3 py-2 bg-white rounded">
										{feature.rolloverTitle}
									</div>
									<div className="c-feature-tooltip__arrow absolute z-10 bg-white"></div>
								</div>
							</React.Fragment>
						) : (
							// Element to occupy as much space as the rollover info icon, so all feature text lines up
							<div className="px-1">
								<div style={{ width: '14px' }}></div>
							</div>
						)}
					</div>
					{feature.rolloverTitle && feature.rolloverTitle.length > 0 ? (
						<span aria-hidden="true">{feature.featureName}</span>
					) : (
						<React.Fragment>{feature.featureName}</React.Fragment>
					)}
				</div>
			</th>
			<td className="px-1 py-2 pr-5 text-sm text-right">
				{feature.featureValue}
			</td>
		</tr>
	);
};

export default ProductSpecFeature;
